import React, { useEffect, useState } from 'react';
import { LifeBuoy, Plus, Clock, CheckCircle2, ChevronRight, ArrowLeft, X, Package, ShoppingCart, AlertTriangle, Send, Search } from 'lucide-react';
import { useTickets, useTags, addTicket, replyToTicket, Ticket as TicketType, TicketStatus, Tag } from '../store/tickets';
import { TicketSidebar } from '../operator/Ticket';
const SHIPMENT_OPTIONS: {
  id: string;
  destination: string;
}[] = [{
  id: 'SH-2840-119',
  destination: 'Galleria Vittorio Emanuele, Milano'
}, {
  id: 'SH-2840-118',
  destination: 'Via del Corso 88, Roma'
}, {
  id: 'SH-2840-117',
  destination: 'Via Roma 24, Torino'
}, {
  id: 'SH-2840-116',
  destination: 'Via Etnea 102, Catania'
}, {
  id: 'SH-2840-115',
  destination: 'Via Toledo 145, Napoli'
}];
const STATUS_CONFIG: Record<TicketStatus, {
  bg: string;
  text: string;
  border: string;
  icon: React.ElementType;
}> = {
  Aperto: {
    bg: 'bg-[#EF4444]/10',
    text: 'text-[#EF4444]',
    border: 'border-[#EF4444]/30',
    icon: AlertTriangle
  },
  'In lavorazione': {
    bg: 'bg-[#F5A524]/10',
    text: 'text-[#F5A524]',
    border: 'border-[#F5A524]/30',
    icon: Clock
  },
  'In attesa': {
    bg: 'bg-[#F5A524]/10',
    text: 'text-[#F5A524]',
    border: 'border-[#F5A524]/30',
    icon: Clock
  },
  Risolto: {
    bg: 'bg-[#D4FF3A]/10',
    text: 'text-[#D4FF3A]',
    border: 'border-[#D4FF3A]/30',
    icon: CheckCircle2
  },
  Chiuso: {
    bg: 'bg-[#1F1F1F]',
    text: 'text-[#9A9A9A]',
    border: 'border-[#262626]',
    icon: CheckCircle2
  }
};
export function ClientTickets({
  initialSelectedId
}: {
  initialSelectedId?: string | null;
} = {}) {
  const tickets = useTickets();
  const tags = useTags();
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
  useEffect(() => {
    if (initialSelectedId) setSelectedId(initialSelectedId);
  }, [initialSelectedId]);
  const [isCreating, setIsCreating] = useState(false);
  const [query, setQuery] = useState('');
  const selected = tickets.find(t => t.id === selectedId) || null;
  if (selected) {
    return <TicketDetail ticket={selected} tags={tags} onBack={() => setSelectedId(null)} data-id="element-580" />;
  }
  const filtered = tickets.filter(t => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return t.id.toLowerCase().includes(q) || t.subject.toLowerCase().includes(q) || t.refId.toLowerCase().includes(q);
  });
  return <div className="space-y-6" data-id="element-581">
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-582">
        <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-583">
          Ticket
        </h1>
        <button onClick={() => setIsCreating(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-584">
          <Plus className="h-4 w-4" data-id="element-585" />
          Nuovo ticket
        </button>
      </div>

      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-586">
        <div className="p-4 sm:p-5 border-b border-[#1F1F1F] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-587">
          <div className="text-xs text-[#9A9A9A]" data-id="element-588">{filtered.length} ticket</div>
          <div className="relative w-full sm:w-64" data-id="element-589">
            <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-590" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca ticket..." className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F] text-xs text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/30" data-id="element-591" />
          </div>
        </div>

        {filtered.length === 0 ? <div className="p-12 text-center" data-id="element-592">
            <LifeBuoy className="h-8 w-8 text-[#6B6B6B] mx-auto mb-3" data-id="element-593" />
            <p className="text-sm text-[#9A9A9A]" data-id="element-594">Nessun ticket aperto.</p>
          </div> : <div className="overflow-x-auto" data-id="element-595">
            <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-596">
              <thead data-id="element-597">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-598">
                  <th className="px-5 py-3 font-medium" data-id="element-599">ID</th>
                  <th className="px-5 py-3 font-medium" data-id="element-600">Oggetto</th>
                  <th className="px-5 py-3 font-medium" data-id="element-601">Categoria</th>
                  <th className="px-5 py-3 font-medium" data-id="element-602">Riferimento</th>
                  <th className="px-5 py-3 font-medium" data-id="element-603">Ultimo agg.</th>
                  <th className="px-5 py-3 font-medium" data-id="element-604">Stato</th>
                  <th className="px-5 py-3 font-medium w-8" data-id="element-605" />
                </tr>
              </thead>
              <tbody data-id="element-606">
                {filtered.map(t => {
              const cfg = STATUS_CONFIG[t.status];
              const Icon = cfg.icon;
              const tag = tags.find(tg => tg.id === t.tagId);
              return <tr key={t.id} onClick={() => setSelectedId(t.id)} className="border-b border-[#1F1F1F] last:border-0 hover:bg-[#0A0A0A] transition-colors cursor-pointer" data-id="element-607">
                      <td className="px-5 py-3.5 font-mono text-xs text-[#FAFAFA]" data-id="element-608">
                        {t.id}
                      </td>
                      <td className="px-5 py-3.5 text-[#FAFAFA] max-w-[280px] truncate" data-id="element-609">
                        {t.subject}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-610">
                        {tag && <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border" style={{
                    backgroundColor: `${tag.color}15`,
                    color: tag.color,
                    borderColor: `${tag.color}30`
                  }} data-id="element-611">
                            {tag.name}
                          </span>}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-612">
                        <div className="flex items-center gap-1.5 text-xs text-[#9A9A9A]" data-id="element-613">
                          {t.refType === 'shipment' ? <Package className="h-3.5 w-3.5" data-id="element-614" /> : <ShoppingCart className="h-3.5 w-3.5" data-id="element-615" />}
                          <span className="font-mono" data-id="element-616">{t.refId}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-xs font-mono text-[#9A9A9A]" data-id="element-617">
                        {t.lastUpdate}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-618">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${cfg.bg} ${cfg.text} ${cfg.border}`} data-id="element-619">
                          <Icon className="h-3 w-3" data-id="element-620" />
                          {t.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right" data-id="element-621">
                        <ChevronRight className="h-4 w-4 text-[#6B6B6B] inline" data-id="element-622" />
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {isCreating && <NewTicketModal tags={tags} onClose={() => setIsCreating(false)} data-id="element-623" />}
    </div>;
}
function TicketDetail({
  ticket,
  tags,
  onBack
}: {
  ticket: TicketType;
  tags: Tag[];
  onBack: () => void;
}) {
  const [replyText, setReplyText] = useState('');
  const tag = tags.find(t => t.id === ticket.tagId);
  const handleReply = () => {
    if (!replyText.trim()) return;
    replyToTicket(ticket.id, replyText.trim(), 'customer');
    setReplyText('');
  };
  const canReply = ticket.status !== 'Risolto' && ticket.status !== 'Chiuso';
  return <div className="space-y-6" data-id="element-624">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#9A9A9A] hover:text-[#FAFAFA] transition-colors" data-id="element-625">
        <ArrowLeft className="h-4 w-4" data-id="element-626" />
        <span data-id="element-627">Torna ai ticket</span>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start" data-id="element-628">
        {/* Sidebar */}
        <TicketSidebar ticket={ticket} tag={tag} viewer="customer" data-id="element-629" />

        {/* Conversation */}
        <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden flex flex-col h-[600px]" data-id="element-630">
          <div className="p-5 border-b border-[#262626] shrink-0" data-id="element-631">
            <h2 className="text-base font-semibold text-[#FAFAFA] font-heading truncate" data-id="element-632">
              {ticket.subject}
            </h2>
            <div className="flex items-center gap-2 text-[11px] font-mono text-[#6B6B6B] mt-1" data-id="element-633">
              <span data-id="element-634">{ticket.id}</span>
              <span data-id="element-635">·</span>
              <span data-id="element-636">Aperto il {ticket.createdAt}</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-6 bg-[#0A0A0A]" data-id="element-637">
            {ticket.messages.map(m => {
            const isCustomer = m.sender === 'customer';
            return <div key={m.id} className={`flex flex-col max-w-[85%] ${isCustomer ? 'ml-auto items-end' : 'mr-auto items-start'}`} data-id="element-638">
                  <div className="flex items-center gap-2 mb-1.5 px-1" data-id="element-639">
                    <span className="text-xs font-medium text-[#FAFAFA]" data-id="element-640">
                      {isCustomer ? 'Tu' : 'Supporto'}
                    </span>
                    <span className="text-[10px] font-mono text-[#6B6B6B]" data-id="element-641">
                      {m.timestamp}
                    </span>
                  </div>
                  <div className={`p-3.5 rounded-2xl text-sm ${isCustomer ? 'bg-[#D4FF3A] text-[#0A0A0A] rounded-tr-sm' : 'bg-[#1F1F1F] text-[#FAFAFA] rounded-tl-sm'}`} data-id="element-642">
                    {m.text}
                  </div>
                </div>;
          })}
          </div>

          {canReply ? <div className="p-4 border-t border-[#262626] bg-[#181818] shrink-0" data-id="element-643">
              <div className="flex items-end gap-3" data-id="element-644">
                <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Scrivi un messaggio..." className="flex-1 bg-[#0A0A0A] border border-[#262626] rounded-xl p-3 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40 resize-none min-h-[80px]" data-id="element-645" />
                <button onClick={handleReply} disabled={!replyText.trim()} className="p-3 rounded-xl bg-[#D4FF3A] text-[#0A0A0A] hover:bg-[#c2ed28] transition disabled:opacity-50 disabled:cursor-not-allowed shrink-0" data-id="element-646">
                  <Send className="h-5 w-5" data-id="element-647" />
                </button>
              </div>
            </div> : <div className="p-4 border-t border-[#262626] bg-[#181818] shrink-0 text-center" data-id="element-648">
              <div className="text-xs text-[#6B6B6B]" data-id="element-649">
                Questo ticket è stato chiuso.
              </div>
            </div>}
        </div>
      </div>
    </div>;
}
function NewTicketModal({
  tags,
  onClose
}: {
  tags: Tag[];
  onClose: () => void;
}) {
  const [tagId, setTagId] = useState(tags[0]?.id || '');
  const [refId, setRefId] = useState(SHIPMENT_OPTIONS[0]?.id || '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const handleSubmit = () => {
    if (!subject.trim() || !message.trim()) return;
    addTicket({
      subject: subject.trim(),
      tagId,
      refType: 'shipment',
      refId: refId || 'N/A',
      status: 'Aperto',
      customerName: 'Cliente Demo',
      customerEmail: '[email protected]',
      messages: [{
        id: 'm1',
        sender: 'customer',
        text: message.trim(),
        timestamp: 'Ora'
      }],
      returnApproval: tagId === 'reso' ? {
        status: 'pending'
      } : undefined
    });
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-650">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-651" />
      <div className="relative w-full max-w-lg bg-[#0A0A0A] border border-[#262626] rounded-xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-652">
        <div className="p-5 border-b border-[#262626] flex items-center justify-between shrink-0" data-id="element-653">
          <h2 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-654">
            Nuovo ticket
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition" data-id="element-655">
            <X className="h-5 w-5" data-id="element-656" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4" data-id="element-657">
          <div data-id="element-658">
            <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-659">
              Categoria
            </label>
            <select value={tagId} onChange={e => setTagId(e.target.value)} className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-660">
              {tags.map(t => <option key={t.id} value={t.id} data-id="element-661">
                  {t.name}
                </option>)}
            </select>
          </div>

          <div data-id="element-662">
            <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-663">
              Spedizione
            </label>
            <select value={refId} onChange={e => setRefId(e.target.value)} className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-664">
              {SHIPMENT_OPTIONS.map(s => <option key={s.id} value={s.id} data-id="element-665">
                  {s.id} — {s.destination}
                </option>)}
            </select>
          </div>

          <div data-id="element-666">
            <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-667">
              Oggetto
            </label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Sintesi del problema" className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-668" />
          </div>

          <div data-id="element-669">
            <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-670">
              Messaggio
            </label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Descrivi il problema nel dettaglio..." rows={4} className="w-full bg-[#181818] border border-[#262626] rounded-lg p-3 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40 resize-none" data-id="element-671" />
          </div>
        </div>

        <div className="p-4 border-t border-[#262626] bg-[#181818] shrink-0 flex items-center justify-end gap-2" data-id="element-672">
          <button onClick={onClose} className="px-3.5 py-2 rounded-lg text-sm font-medium text-[#9A9A9A] hover:text-[#FAFAFA] transition" data-id="element-673">
            Annulla
          </button>
          <button onClick={handleSubmit} disabled={!subject.trim() || !message.trim()} className="px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition disabled:opacity-50" data-id="element-674">
            Invia ticket
          </button>
        </div>
      </div>
    </div>;
}